import "./fleetHistoryJourneyScrubber.component.scss";

import * as angular from "angular";
import * as moment from "moment";
import { VeriDate } from "@services/veriDate.service";
import { IJourney } from "@interfaces/IJourney";
import { IVehicle } from "@interfaces/IVehicle";

interface IFleetHistoryScrubberJourney extends IJourney {
    styleLeft?: string;
    styleWidth?: string;
}

interface IFleetHistoryVehicleJourneys {
    vehicleId?: number;
    vehicle?: IVehicle;
    journeys: IFleetHistoryScrubberJourney[];
    totalElapsedMinutes?: number;
}

interface ITimelineElementDetails {
    timelineLeftX: number;
    timelineRightX: number;
    timelineWidth: number;
    additionalWidthBloatLeft: number;
    additionalWidthBloatRight: number;
    minutesPerPixel: number;
}

enum PlaybackDirection {
    Forward,
    Backward
}

interface ITimelineTemporalHeader {
    label?: string;
    leftOffset?: number;
    rightOffset?: number;
}

interface IComponentBindings {
    journeys: IJourney[];
    startMinute: number;
    endMinute: number;
    disableScrubber: boolean;

    onMouseMove: (args: { time: moment.Moment }) => void;
    onMouseClick: (args: { time: moment.Moment }) => void;
    onSelectedTimeRange: (args: { startMoment: moment.Moment, endMoment: moment.Moment }) => void;
}

class FleetHistoryJourneyScrubberController implements angular.IComponentController, IComponentBindings {

    // bindings
    journeys: IJourney[] = [];
    startMinute: number;
    endMinute: number;
    disableScrubber: boolean = false;

    onMouseMove: (args: { time: moment.Moment }) => void;
    onMouseClick: (args: { time: moment.Moment }) => void;
    onSelectedTimeRange: (args: { startMoment: moment.Moment, endMoment: moment.Moment }) => void;

    // configuration
    indicatorLabelPixelWidth: number = 46;

    // calculations
    hasDataAndEnabled: boolean = false;
    temporalHeaders: ITimelineTemporalHeader[] = [];
    vehicleJourneyModels: IFleetHistoryVehicleJourneys[] = [];

    totalMinutesInRange: number;
    startMoment: moment.Moment;
    endMoment: moment.Moment;

    timelineElementDetails: ITimelineElementDetails = {
        timelineLeftX: null,
        timelineRightX: null,
        timelineWidth: null,
        additionalWidthBloatLeft: null,
        additionalWidthBloatRight: null,
        minutesPerPixel: null
    };

    // interaction
    throttleMouseMove: ($event: MouseEvent) => void;
    hoveredMoment: moment.Moment;
    hoveredMinuteOfDay: number;
    hoveringAJourney: boolean = false;

    selectingTimelinePeriod: boolean = false;
    selectionAreaWidthPixels: number;
    selectionAreaLeftPixels: number;
    selectionStartIndicatorLeftPixels: number;
    selectionEndIndicatorLeftPixels: number;
    selectionStartMoment: moment.Moment;
    selectionEndMoment: moment.Moment;

    cachedMouseX: number;
    latestMouseDownX: number;
    latestMouseUpX: number;

    isMouseDown: boolean = false;
    lastMouseDownX: number;
    hoverIndicatorLeftPixels: number;
    mouseOutsideTimeline: boolean = false;

    // playback
    playbackInterval: angular.IPromise<any>;
    playbackDirection: PlaybackDirection = PlaybackDirection.Forward;
    playbackSpeed: number = 200;
    currentPlaybackPixel: number = 0;
    isPlayingBackHistory: boolean = false;

    static $inject = ["$window", "$element", "veriDate", "$interval", "$throttle"];

    constructor(
        private readonly $window: angular.IWindowService,
        private readonly $element: JQLite,
        private readonly veriDate: VeriDate,
        private readonly $interval: angular.IIntervalService,
        private readonly $throttle: any) {
    }

    $onInit(): void {
        this.throttleMouseMove = this.$throttle(($event: MouseEvent) => { this.onTimeSelectorMouseMove($event, false); }, 10);

        this.bindWindowResizeEvent();
        this.gatherDOMElementBasedDetails();
        this.resetHoverIndicator();
    }

    $onDestroy(): void {
        this.unbindWindowResizeEvent();
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if (onChangesObj["journeys"]) {
            this.processJourneys(this.journeys);
        }

        if (onChangesObj["startMinute"] || onChangesObj["endMinute"]) {
            this.updateCoreTimelineStructure();
            this.processMouseTimelinePosition(this.cachedMouseX);
        }
    }

    resetPlaybackHistoryToStart(): void {
        this.playbackDirection = PlaybackDirection.Forward;
        this.currentPlaybackPixel = 0;
        this.processMouseTimelinePosition(this.timelineElementDetails.timelineLeftX + this.timelineElementDetails.additionalWidthBloatLeft);
    }

    fastRewindPlaybackHistory(): void {
        this.playbackDirection = PlaybackDirection.Backward;
        this.playbackSpeed = 80;
    }

    fastForwardPlaybackHistory(): void {
        this.playbackDirection = PlaybackDirection.Forward;
        this.playbackSpeed = 80;
    }

    playPausePlaybackHistory(): void {
        if (this.isPlayingBackHistory) {
            this.pausePlaybackHistory();
            return;
        }

        this.isPlayingBackHistory = true;
        this.playbackDirection = PlaybackDirection.Forward;
        this.playbackSpeed = this.getNormalPlaybackSpeed();

        this.playbackInterval = this.$interval(() => {
            this.updatePlaybackPixel();
            this.processMouseTimelinePosition(this.timelineElementDetails.timelineLeftX + this.currentPlaybackPixel + this.timelineElementDetails.additionalWidthBloatLeft);
        }, this.playbackSpeed);
    }

    pausePlaybackHistory(): void {
        this.cancelExistingPlaybackInterval();
        this.isPlayingBackHistory = false;
    }

    cancelPlaybackHistory(): void {
        this.cancelExistingPlaybackInterval();
    }

    onTimeSelectorMouseMove($event: MouseEvent, requiresDebounce = true): void {
        if (this.disableScrubber) {
            return;
        }

        if (requiresDebounce || this.isPlayingBackHistory) {
            this.throttleMouseMove($event);
            return;
        }

        if (this.isMouseDown && Math.abs($event.clientX - this.lastMouseDownX) > 6) {
            this.selectingTimelinePeriod = true;

            if ($event.clientX >= this.lastMouseDownX) {
                this.selectionStartMoment = this.getMomentFromRawMouseX(this.lastMouseDownX);
                this.selectionEndMoment = this.getMomentFromRawMouseX($event.clientX);
            } else {
                this.selectionStartMoment = this.getMomentFromRawMouseX($event.clientX);
                this.selectionEndMoment = this.getMomentFromRawMouseX(this.lastMouseDownX);
            }

            // move the below and divert into main processMouseTimelinePosition?
            this.updateSelectionStartIndicatorUsingMoment(this.selectionStartMoment);
        }

        this.updateCoreTimelineStructure();
        this.processMouseTimelinePosition($event.clientX);
    }

    onTimeSelectorMouseDown($event: MouseEvent): void {
        if (this.disableScrubber) {
            return;
        }

        if (this.getRelativeMouseXFromMouseX($event?.clientX) >= 0) {
            this.isMouseDown = true;
            this.lastMouseDownX = $event.clientX;
        }
    }

    onTimeSelectorMouseUp($event: MouseEvent): void {
        if (this.disableScrubber) {
            return;
        }

        this.isMouseDown = false;

        this.processTimelineMouseUp($event);
    }

    onTimeSelectorMouseLeave($event: MouseEvent): void {
        if (this.disableScrubber) {
            return;
        }

        this.cancelTimelineSelection();
    }

    // -----

    private bindWindowResizeEvent(): void {
        angular.element(this.$window).on('resize', () => {
            this.gatherDOMElementBasedDetails();
        });
    }

    private unbindWindowResizeEvent(): void {
        angular.element(this.$window).off('resize');
    }

    private createTemporalHeaders(): void {
        this.temporalHeaders = [];

        const numberOfHeaders: number = 6;
        const newTemporalHeaders: ITimelineTemporalHeader[] = [];
        const minutesPerSegment: number = this.totalMinutesInRange / numberOfHeaders;

        for (var i = 0; i < numberOfHeaders; i++) {
            const minutesIntoTotal: number = minutesPerSegment * i;
            const percentageIntoRange: number = (minutesIntoTotal / this.totalMinutesInRange) * 100;
            const headerMoment: moment.Moment = this.veriDate.createMoment(new Date()).set({ hour: 0, minute: this.startMinute + minutesIntoTotal });

            newTemporalHeaders.push({
                label: this.veriDate.formatDate(headerMoment, "time"),
                leftOffset: percentageIntoRange
            });
        }

        this.temporalHeaders = [...newTemporalHeaders];
    }

    private gatherDOMElementBasedDetails(): void {
        const boundingClientRect: DOMRect = this.$element[0].getBoundingClientRect();

        const scrollRect: any = this.$element.find(".md-virtual-repeat-scroller")[0];
        const journeyContainerIsScrollable: boolean = (scrollRect) ? (scrollRect?.clientHeight < scrollRect?.scrollHeight) : false;

        this.timelineElementDetails.timelineLeftX = boundingClientRect.left;
        this.timelineElementDetails.timelineRightX = boundingClientRect.right;

        this.timelineElementDetails.additionalWidthBloatLeft = 90 + 10; // reg block width + padding
        this.timelineElementDetails.additionalWidthBloatRight = (journeyContainerIsScrollable) ? 18 : 10; // padding, add 8px for scroller
        this.timelineElementDetails.timelineWidth = this.$element[0].offsetWidth - (this.timelineElementDetails.additionalWidthBloatLeft + this.timelineElementDetails.additionalWidthBloatRight);
        this.timelineElementDetails.minutesPerPixel = (this.endMinute - this.startMinute) / this.timelineElementDetails.timelineWidth;
    }

    private processJourneys(journeys: IJourney[]): void {
        this.updateCoreTimelineStructure();

        this.vehicleJourneyModels = [];

        if (!journeys?.length) {
            return;
        }

        const newVehicleJourneyModels: IFleetHistoryVehicleJourneys[] = [...this.vehicleJourneyModels];
        journeys.forEach(journey => {
            const styledJourney: IFleetHistoryScrubberJourney = journey;
            const vehicle = styledJourney?.vehicle;

            if (vehicle) {
                const existingJourneyModel = newVehicleJourneyModels.find(journeyModel => { return journeyModel.journeys.find(journey => journey?.VehicleId === vehicle?.Id) });
                if (existingJourneyModel) {
                    existingJourneyModel.journeys.push(styledJourney);
                    existingJourneyModel.totalElapsedMinutes += styledJourney.totalElapsedMinutes;
                } else {
                    newVehicleJourneyModels.push({
                        vehicleId: styledJourney?.VehicleId,
                        vehicle: styledJourney?.vehicle,
                        journeys: [styledJourney],
                        totalElapsedMinutes: styledJourney.totalElapsedMinutes
                    });
                }
            }

            styledJourney.styleWidth = ((journey.totalElapsedMinutes / this.totalMinutesInRange) * 100) + '%';
            styledJourney.styleLeft = (((journey.startMinuteOfDay - this.startMinute) / (this.endMinute - this.startMinute)) * 100) + '%';
        });

        newVehicleJourneyModels.sort((model1, model2) => (model1.totalElapsedMinutes > model2.totalElapsedMinutes) ? -1 : 1);

        this.vehicleJourneyModels = [...newVehicleJourneyModels];
    }

    private resetHoverIndicator(): void {
        this.hoverIndicatorLeftPixels = this.timelineElementDetails.additionalWidthBloatLeft;
        this.hoveredMoment = this.startMoment;
    }

    private updateCoreTimelineStructure(): void {
        this.totalMinutesInRange = ((this.endMinute - this.startMinute) < 0) ? 0 : (this.endMinute - this.startMinute);
        this.startMoment = this.veriDate.createMoment(new Date()).set({ hour: 0, minute: this.startMinute });
        this.endMoment = this.veriDate.createMoment(new Date()).set({ hour: 0, minute: this.endMinute });

        this.gatherDOMElementBasedDetails();
        this.createTemporalHeaders();
        this.hasDataAndEnabled = this.vehicleJourneyModels.length > 0 && this.totalMinutesInRange > 0 && !this.disableScrubber;
    }

    private getNormalPlaybackSpeed(): number {
        return 120; // replace with constant speed math
    }

    private updatePlaybackPixel(): void {
        if (this.playbackDirection === PlaybackDirection.Forward) {
            this.currentPlaybackPixel++;
        } else {
            this.currentPlaybackPixel--;
        }
    }

    private cancelExistingPlaybackInterval(): void {
        if (this.playbackInterval) {
            this.$interval.cancel(this.playbackInterval);
        }
    }

    private processMouseTimelinePosition(mouseX: number = 0, usingRelativeMouse: boolean = false, preventEmittingMoveEvent: boolean = false): void {
        let relativeMouseX: number = (usingRelativeMouse) ? mouseX : this.getRelativeMouseXFromMouseX(mouseX);
        const mouseIsToTheLeftOfTimeline: boolean = relativeMouseX < 0;

        if (mouseIsToTheLeftOfTimeline) {
            this.mouseOutsideTimeline = true;
            this.updateHoverIndicatorPositionUsingMoment(this.startMoment);
            return;
        }

        this.mouseOutsideTimeline = false;
        this.cachedMouseX = mouseX;

        this.hoveredMinuteOfDay = this.getMinuteOfDayFromRelativeMouseX(relativeMouseX);
        this.hoveredMoment = this.veriDate.createMoment(new Date()).set({ hour: 0, minute: this.hoveredMinuteOfDay });

        this.updateSelectionEndIndicatorUsingMoment(this.hoveredMoment);
        this.updateHoverIndicatorPositionUsingMoment(this.hoveredMoment);
        this.updateSelectionArea();

        if (!preventEmittingMoveEvent) {
            this.onMouseMove({ time: this.hoveredMoment });
        }
    }

    private getRelativeMouseXFromMouseX(mouseX: number): number {
        return Math.round(mouseX - this.timelineElementDetails.timelineLeftX - this.timelineElementDetails.additionalWidthBloatLeft);
    }

    private getMinuteOfDayFromRelativeMouseX(relativeMouseX: number): number {
        return this.startMinute + (relativeMouseX * this.timelineElementDetails.minutesPerPixel);
    }

    private updateSelectionArea(): void {
        if (!this.selectingTimelinePeriod || !this.selectionStartMoment) {
            return;
        }

        const startMouseMinuteOfDay: number = this.getMinuteOfDayFromMoment(this.selectionStartMoment);
        const currentMouseMinuteOfDay: number = this.getMinuteOfDayFromMoment(this.selectionEndMoment);
        const totalSelectedMinutes: number = currentMouseMinuteOfDay - startMouseMinuteOfDay;
        const selectionPercentageOfTotalMinuteRange: number = (totalSelectedMinutes / this.totalMinutesInRange) * 100;

        this.selectionAreaLeftPixels = this.selectionStartIndicatorLeftPixels;
        this.selectionAreaWidthPixels = this.timelineElementDetails.timelineWidth * (selectionPercentageOfTotalMinuteRange / 100);
    }

    private getMinuteOfDayFromMoment(newMoment: moment.Moment): number {
        return (newMoment.hour() * 60) + newMoment.minute();
    }

    private getPercentageOnTimelineFromMoment(newMoment: moment.Moment): number {
        const momentMinuteOfDay: number = this.getMinuteOfDayFromMoment(newMoment);
        return ((momentMinuteOfDay - this.startMinute) / this.totalMinutesInRange) * 100;
    }

    private getLeftPixelsForTimelineFromMoment(newMoment: moment.Moment): number {
        const percentageMomentIntoTimeline: number = this.getPercentageOnTimelineFromMoment(newMoment);
        const totalConsumedMinutes: number = this.totalMinutesInRange * (percentageMomentIntoTimeline / 100);

        const leftPixels: number = (totalConsumedMinutes / this.timelineElementDetails.minutesPerPixel)
        return leftPixels + this.timelineElementDetails.additionalWidthBloatLeft;
    }

    private updateSelectionStartIndicatorUsingMoment(newMoment: moment.Moment): void {
        if (!this.selectingTimelinePeriod) {
            return;
        }

        this.selectionStartIndicatorLeftPixels = this.getLeftPixelsForTimelineFromMoment(newMoment);
    }

    private updateSelectionEndIndicatorUsingMoment(newMoment: moment.Moment): void {
        if (!this.selectingTimelinePeriod) {
            return;
        }

        this.selectionEndIndicatorLeftPixels = this.getLeftPixelsForTimelineFromMoment(newMoment);
    }

    private updateHoverIndicatorPositionUsingMoment(newMoment: moment.Moment): void {
        if (this.selectingTimelinePeriod) {
            return;
        }

        this.hoverIndicatorLeftPixels = this.getLeftPixelsForTimelineFromMoment(newMoment);
        this.detectIfIndicatorWithinAJourney();
    }

    private detectIfIndicatorWithinAJourney(): void {
        this.hoveringAJourney = this.journeys.filter(journey => this.hoveredMinuteOfDay >= journey.startMinuteOfDay && this.hoveredMinuteOfDay <= journey.endMinuteOfDay)?.length > 0;
    }

    private processTimelineMouseUp($event: MouseEvent): void {
        this.selectingTimelinePeriod = false;

        this.processTimelineSelection($event.clientX);
    }

    private processTimelineSelection(mouseX: number = -1): void {
        if (mouseX === -1) {
            return;
        }

        if (this.selectionStartMoment && this.selectionEndMoment) {
            this.onSelectedTimeRange({ startMoment: this.selectionStartMoment, endMoment: this.selectionEndMoment });
        }

        this.cancelTimelineSelection();
    }

    private getMomentFromRawMouseX(mouseX: number): moment.Moment {
        if (!mouseX) {
            return null;
        }

        let relativeMouseX: number = this.getRelativeMouseXFromMouseX(mouseX);
        const mouseMinuteOfDay: number = this.getMinuteOfDayFromRelativeMouseX(relativeMouseX);
        const hoveredMoment: moment.Moment = this.veriDate.createMoment(new Date()).set({ hour: 0, minute: mouseMinuteOfDay });

        return hoveredMoment;
    }

    private cancelTimelineSelection(): void {
        this.isMouseDown = false;
        this.selectingTimelinePeriod = false;
        this.selectionStartIndicatorLeftPixels = null;
        this.selectionEndIndicatorLeftPixels = null;
        this.selectionAreaWidthPixels = null;
        this.selectionAreaLeftPixels = null;
        this.selectionStartMoment = null;
        this.selectionEndMoment = null;
    }
}

export class FleetHistoryJourneyScrubberComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            journeys: "<",
            startMinute: "<",
            endMinute: "<",
            disableScrubber: "<?",
            onMouseMove: "&",
            onMouseClick: "&",
            onSelectedTimeRange: "&"
        };

        this.controller = FleetHistoryJourneyScrubberController;
        this.template = require("./fleetHistoryJourneyScrubber.component.html");
    }
}
